import { useState, useEffect, useCallback } from 'react';
import { BarChart3, Users, TrendingUp, GitCompare } from 'lucide-react';
import { runStackRankingTool } from '../../tools/stackRanking';
import type { ToolConfig } from '../../harmonizer/types';
import { RankingView } from './components/RankingView';
import { ConsensusView } from './components/ConsensusView';
import { ComparisonView } from './components/ComparisonView';
import type { RankingItem, Participant, ViewMode, ConsensusScore } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, useToolSituationalContext, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { StackRankingLive } from '../../components/prioritization/session/liveExperience/liveTools/StackRankingLive';

const PARTICIPANT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

function StackRankingContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  const situationalContext = useToolSituationalContext();
  const completeLabel = situationalContext?.completion?.label ?? 'Save Session Result';
  
  const [items, setItems] = useState<RankingItem[]>([]);

  const [participants, setParticipants] = useState<Participant[]>([
    {
      id: 'p1',
      name: 'You',
      color: PARTICIPANT_COLORS[0],
      ranking: [],
    },
  ]);

  const [viewMode, setViewMode] = useState<ViewMode>('ranking');
  const [newItemName, setNewItemName] = useState('');
  const [newParticipantName, setNewParticipantName] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});
  
  // Handler for importing items from workspace (app mode)
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: RankingItem[] = toolItems.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    
    setItems(prev => [...prev, ...newItems]);
    
    // Add new items to first participant's ranking
    const newItemIds = newItems.map(i => i.id);
    setParticipants(prev => prev.map((p, idx) => 
      idx === 0 ? { ...p, ranking: [...p.ranking, ...newItemIds] } : p
    ));
  }, []);
  
  // Consensus rankings come from the canonical engine adapter
  // (src/tools/stackRanking.ts): the page collects participant orderings; the
  // engine owns Borda scoring, average ranks, and consensus normalization.
  const [consensusRankings, setConsensusRankings] = useState<ConsensusScore[]>([]);
  useEffect(() => {
    if (participants.length === 0 || items.length === 0) {
      setConsensusRankings([]);
      return;
    }
    let cancelled = false;
    void runStackRankingTool(
      items.map((item) => ({ id: item.id, label: item.name, attributes: {} })),
      null,
      {
        participant_count: participants.length,
        participant_rankings: participants.map((p) => ({
          participant_id: p.id,
          participant_name: p.name,
          ranking: p.ranking,
        })),
      } as unknown as ToolConfig
    ).then((scoreSet) => {
      if (cancelled) return;
      setConsensusRankings(
        scoreSet.items.map((s) => ({
          itemId: s.item_id as string,
          itemName: items.find((i) => i.id === s.item_id)?.name || (s.item_id as string),
          averageRank: s.average_rank as number,
          bordaScore: s.score as number,
          consensusScore: s.consensus_score as number,
          standardDeviation: s.standard_deviation as number,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [participants, items]);

  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Use consensus if multiple participants, otherwise first participant's ranking
    const consensusRanks = participants.length > 1 ? consensusRankings : null;
    
    if (consensusRanks && consensusRanks.length > 0) {
      return consensusRanks.map((cr, idx) => ({
        id: sourceIdMap[cr.itemId] || cr.itemId,
        name: items.find(i => i.id === cr.itemId)?.name || cr.itemId,
        rank: idx + 1,
        score: cr.averageRank ? (items.length - cr.averageRank + 1) : undefined, // Invert so higher = better
        scoreBreakdown: {
          averageRank: cr.averageRank,
          standardDeviation: cr.standardDeviation,
        },
        metadata: {
          averageRank: cr.averageRank,
          standardDeviation: cr.standardDeviation,
        }
      }));
    }
    
    // Single participant - use their ranking
    const ranking = participants[0]?.ranking || [];
    return ranking.map((itemId, idx) => ({
      id: sourceIdMap[itemId] || itemId,
      name: items.find(i => i.id === itemId)?.name || itemId,
      rank: idx + 1,
      score: items.length - idx, // Higher rank = higher score
    }));
  }, [participants, items, sourceIdMap, consensusRankings]);

  // Check if results are ready to save
  const myRanking = participants[0]?.ranking ?? [];
  const hasResults =
    items.length > 0 &&
    (isLiveSession
      ? myRanking.length === items.length
      : participants.some(p => p.ranking.length > 0));

  // Add new item
  const handleAddItem = () => {
    if (!newItemName.trim()) return;
    const newItem: RankingItem = {
      id: `item-${Date.now()}`,
      name: newItemName.trim(),
    };
    setItems([...items, newItem]);
    setNewItemName('');
    setShowAddItem(false);
  };

  // Remove item
  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter((item) => item.id !== itemId));
    // Remove from all participant rankings
    setParticipants(
      participants.map((p) => ({
        ...p,
        ranking: p.ranking.filter((id) => id !== itemId),
      }))
    );
  };

  // Add new participant
  const handleAddParticipant = () => {
    if (!newParticipantName.trim()) return;
    const colorIndex = participants.length % PARTICIPANT_COLORS.length;
    const newParticipant: Participant = {
      id: `p-${Date.now()}`,
      name: newParticipantName.trim(),
      color: PARTICIPANT_COLORS[colorIndex],
      ranking: [], // Start with empty ranking
    };
    setParticipants([...participants, newParticipant]);
    setNewParticipantName('');
    setShowAddParticipant(false);
  };

  // Update participant ranking
  const handleRankingChange = (participantId: string, ranking: string[]) => {
    setParticipants(
      participants.map((p) =>
        p.id === participantId ? { ...p, ranking } : p
      )
    );
  };

  // Remove participant
  const handleRemoveParticipant = (participantId: string) => {
    if (participants.length <= 1) return; // Keep at least one participant
    setParticipants(participants.filter((p) => p.id !== participantId));
  };

  return (
    <ToolWrapper
      toolName="Stack Ranking"
      toolDescription="Drag and drop to rank items. Compare rankings and find consensus."
      onImportItems={handleImportItems}
      onGetResults={hasResults ? handleGetResults : undefined}
      itemCount={items.length}
      {...props}
    >
      {isLiveSession && items.length > 0 ? (
        <LiveToolScaffold
          title="Stack ranking"
          subtitle="Drag to order — #1 is your highest priority"
          progressDone={myRanking.length}
          progressTotal={items.length}
        >
          <StackRankingLive
            items={items}
            ranking={myRanking}
            onRankingChange={ranking => handleRankingChange(participants[0].id, ranking)}
          />
        </LiveToolScaffold>
      ) : (
      <div className="space-y-6">
        {/* View Mode Tabs */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)]">
          <div className="border-b border-[var(--rule)] px-4">
            <div className="flex gap-1 overflow-x-auto">
              <button
                onClick={() => setViewMode('ranking')}
                className={`shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
                  viewMode === 'ranking'
                    ? 'text-[var(--text-ter)] border-b-2 border-[var(--slate)]'
                    : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Rankings
                </div>
              </button>
              <button
                onClick={() => setViewMode('consensus')}
                className={`shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
                  viewMode === 'consensus'
                    ? 'text-[var(--text-ter)] border-b-2 border-[var(--slate)]'
                    : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Consensus
                </div>
              </button>
              <button
                onClick={() => setViewMode('compare')}
                className={`shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
                  viewMode === 'compare'
                    ? 'text-[var(--text-ter)] border-b-2 border-[var(--slate)]'
                    : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <GitCompare className="h-4 w-4" />
                  Compare
                </div>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Rankings View */}
            {viewMode === 'ranking' && (
              <div className="space-y-6">
                {/* Add Participant */}
                <div className="bg-[var(--surface)] p-4">
                  {showAddParticipant ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newParticipantName}
                        onChange={(e) => setNewParticipantName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddParticipant()}
                        placeholder="Participant name"
                        className="flex-1 px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                        autoFocus
                      />
                      <button
                        onClick={handleAddParticipant}
                        className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddParticipant(false);
                          setNewParticipantName('');
                        }}
                        className="px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddParticipant(true)}
                      className="w-full p-3 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] transition-colors bg-[var(--bg-raised)]"
                    >
                      + Add Participant
                    </button>
                  )}
                </div>

                {/* Participant Rankings */}
                <div className="grid gap-6">
                  {participants.map((participant) => (
                    <div key={participant.id} className="bg-[var(--surface)] p-4">
                      <RankingView
                        participant={participant}
                        items={items}
                        onRankingChange={handleRankingChange}
                        onAddItem={!isAppMode ? () => setShowAddItem(true) : undefined}
                        onRemoveItem={handleRemoveItem}
                        canEdit={true}
                      />
                      {participants.length > 1 && (
                        <button
                          onClick={() => handleRemoveParticipant(participant.id)}
                          className="mt-4 text-sm text-[var(--coral)] hover:text-[var(--coral)]"
                        >
                          Remove Participant
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Item Modal - Only in standalone mode */}
                {!isAppMode && showAddItem && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-[var(--bg-raised)] p-6 max-w-md w-full mx-4">
                      <h3 className="text-lg font-semibold mb-4">Add Item</h3>
                      <input
                        type="text"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                        placeholder="Item name"
                        className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)] mb-4"
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setShowAddItem(false);
                            setNewItemName('');
                          }}
                          className="px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddItem}
                          className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Consensus View */}
            {viewMode === 'consensus' && (
              <ConsensusView
                participants={participants}
                consensusRankings={consensusRankings}
                items={items}
              />
            )}

            {/* Compare View */}
            {viewMode === 'compare' && (
              <ComparisonView participants={participants} items={items} />
            )}
          </div>
        </div>
        
        {/* Completion prompt */}
        {hasResults && isAppMode && (
          <div className="card bg-[var(--green-dim)] border-[var(--green)]">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-[var(--green)] flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Rankings ready!</h3>
                <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                  Click <strong>"{completeLabel}"</strong> in the header to save your ranked priorities.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </ToolWrapper>
  );
}

export default function StackRanking(props: ToolHostProps) {
  return <StackRankingContent {...props} />;
}


